import React, { useState } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import { Link as RouterLink } from 'react-router-dom';
import LockResetIcon from '@mui/icons-material/LockReset';
import AuthCard from './AuthCard';
import request from '../api';

// Two-step "olvidé mi contraseña" flow shared by the customer and store login forms:
// request a code by email, then submit that code with a new password. Both steps
// respond the same way whether or not the email is registered, so this UI can't be
// used to probe which emails have accounts.
function ForgotPasswordCard({ apiBase, loginPath }) {
    const [step, setStep] = useState('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [busy, setBusy] = useState(false);

    const requestCode = async (e) => {
        e.preventDefault();
        setError('');
        setBusy(true);
        try {
            await request(`${apiBase}/forgot-password`, { method: 'POST', body: { email } });
            setStep('confirm');
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    const resetPassword = async (e) => {
        e.preventDefault();
        setError('');
        if (newPassword !== confirmPassword) {
            setError('La nueva contraseña y su confirmación no coinciden');
            return;
        }
        setBusy(true);
        try {
            await request(`${apiBase}/reset-password`, {
                method: 'POST',
                body: { email, code, new_password: newPassword },
            });
            setNotice('Tu contraseña fue actualizada correctamente.');
            setStep('done');
        } catch (err) {
            setError(err.message);
        } finally {
            setBusy(false);
        }
    };

    if (step === 'done') {
        return (
            <AuthCard sx={{ textAlign: 'center' }}>
                <LockResetIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Alert severity="success" sx={{ mb: 2, textAlign: 'left' }}>{notice}</Alert>
                <Button component={RouterLink} to={loginPath} variant="contained" size="large" fullWidth>
                    Ir a Iniciar Sesión
                </Button>
            </AuthCard>
        );
    }

    if (step === 'confirm') {
        return (
            <AuthCard sx={{ textAlign: 'center' }}>
                <LockResetIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Si el correo <strong>{email}</strong> está registrado, te enviamos un código de 6 dígitos.
                    Ingrésalo junto con tu nueva contraseña.
                </Typography>
                <Box component="form" onSubmit={resetPassword}>
                    <Stack spacing={2}>
                        <TextField
                            label="Código de Verificación"
                            value={code}
                            onChange={(e) => setCode(e.target.value)}
                            fullWidth
                            required
                            autoFocus
                            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                        />
                        <TextField
                            label="Nueva Contraseña"
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Confirmar Nueva Contraseña"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            fullWidth
                            required
                        />
                        <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                            Restablecer Contraseña
                        </Button>
                        {error && <Alert severity="error">{error}</Alert>}
                        <Button color="inherit" onClick={() => setStep('request')}>
                            ¿No recibiste el código? Volver a intentar
                        </Button>
                    </Stack>
                </Box>
            </AuthCard>
        );
    }

    return (
        <AuthCard sx={{ textAlign: 'center' }}>
            <LockResetIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Ingresa el correo electrónico asociado a tu cuenta y te enviaremos un código para
                restablecer tu contraseña.
            </Typography>
            <Box component="form" onSubmit={requestCode}>
                <Stack spacing={2}>
                    <TextField
                        label="Correo Electrónico"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                        required
                        autoFocus
                    />
                    <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
                        Enviar Código
                    </Button>
                    {error && <Alert severity="error">{error}</Alert>}
                </Stack>
            </Box>
            <Typography variant="body2" sx={{ mt: 3 }}>
                <Link component={RouterLink} to={loginPath}>
                    Volver a Iniciar Sesión
                </Link>
            </Typography>
        </AuthCard>
    );
}

export default ForgotPasswordCard;
